// src/components/HenkiLabsSection.jsx
import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";

const HenkiLabsSection = ({ apps = [], onSelectApp }) => {
  const { t } = useLanguage();

  return (
    <section id="labs" className="w-full max-w-7xl mx-auto px-6 py-14 space-y-12">
      {/* HEADER DE LA SECCIÓN CON CONTADOR DE IMPACTO */}
      <div className="relative rounded-[2.5rem] overflow-hidden border-2 border-slate-200 dark:border-white/10 bg-gradient-to-r from-blue-50/80 via-cyan-50/60 to-purple-50/80 dark:from-blue-900/40 dark:via-cyan-900/20 dark:to-purple-900/30 p-8 md:p-12 shadow-lg dark:shadow-xl backdrop-blur-xl transition-colors duration-300">
        <div className="pointer-events-none absolute -right-10 -top-10 w-72 h-72 bg-cyan-400/15 dark:bg-cyan-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -left-10 -bottom-10 w-72 h-72 bg-blue-600/10 dark:bg-blue-600/20 rounded-full blur-3xl" />

        <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 space-y-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-sm">
              <span>🚀</span>
              <span>{t("labs.badge")}</span>
            </span>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
              {t("labs.title")}
            </h2>

            <p className="text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed font-normal max-w-2xl">
              {t("labs.subtitle")}
            </p>
          </div>

          {/* STATS CALLOUT */}
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4">
            <div className="p-5 rounded-2xl border-2 border-cyan-500/30 dark:border-cyan-400/40 bg-white dark:bg-slate-900/90 text-slate-900 dark:text-white shadow-md dark:shadow-lg backdrop-blur-md">
              <span className="text-3xl md:text-4xl font-black text-cyan-600 dark:text-cyan-400">
                {t("labs.statDownloads")}
              </span>
              <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide mt-1">
                {t("labs.statDownloadsLabel")}
              </p>
            </div>

            <div className="p-5 rounded-2xl border-2 border-amber-500/30 dark:border-amber-400/40 bg-white dark:bg-slate-900/90 text-slate-900 dark:text-white shadow-md dark:shadow-lg backdrop-blur-md">
              <span className="text-3xl md:text-4xl font-black text-amber-600 dark:text-amber-400">
                {t("labs.statRating")}
              </span>
              <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide mt-1">
                {t("labs.statRatingLabel")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* VITRINA DE NUESTRAS APPS PROPIAS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {apps.map((app, index) => (
          <motion.div
            key={app.id || index}
            whileHover={{ y: -6 }}
            transition={{ duration: 0.25 }}
            className="
              group
              relative
              rounded-3xl
              border-2
              border-slate-200
              dark:border-white/10
              hover:border-cyan-500
              dark:hover:border-cyan-400
              bg-white
              dark:bg-[#0C1926]
              p-6
              flex
              flex-col
              justify-between
              shadow-md
              hover:shadow-xl
              transition-all
              duration-300
            "
          >
            <div className="space-y-4">
              {/* TOP HEADER */}
              <div className="flex items-center justify-between">
                <div className="w-14 h-14 rounded-2xl p-2.5 bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/15 flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform">
                  <img
                    src={app.icon}
                    alt={app.name}
                    className="w-10 h-10 object-contain drop-shadow"
                  />
                </div>

                <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-500/15 text-cyan-800 dark:text-cyan-300 border border-cyan-500/30">
                  {t(`labs.appItems.${app.id}.downloads`) !== `labs.appItems.${app.id}.downloads`
                    ? t(`labs.appItems.${app.id}.downloads`)
                    : (app.downloads || app.badge)}
                </span>
              </div>

              {/* NAME & TAGLINE */}
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                  {app.name}
                </h3>
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5">
                  {t(`labs.appItems.${app.id}.category`) !== `labs.appItems.${app.id}.category`
                    ? t(`labs.appItems.${app.id}.category`)
                    : app.category}
                </p>
              </div>

              {/* SHORT DESCRIPTION */}
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-normal">
                {t(`labs.appItems.${app.id}.shortDescription`) !== `labs.appItems.${app.id}.shortDescription`
                  ? t(`labs.appItems.${app.id}.shortDescription`)
                  : app.shortDescription}
              </p>

              {/* HIGHLIGHT CHIPS */}
              <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-white/10">
                {(t(`labs.appItems.${app.id}.features`) && Array.isArray(t(`labs.appItems.${app.id}.features`))
                  ? t(`labs.appItems.${app.id}.features`)
                  : (app.features || [])
                ).slice(0, 2).map((feat, idx) => (
                  <p key={idx} className="text-[11px] text-slate-700 dark:text-slate-300 flex items-center gap-1.5 font-medium">
                    <span className="text-cyan-600 dark:text-cyan-400 font-bold">✓</span>
                    <span className="truncate">{feat}</span>
                  </p>
                ))}
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="pt-6 mt-4 border-t border-slate-100 dark:border-white/10 flex items-center gap-2">
              <button
                onClick={() => onSelectApp && onSelectApp(app)}
                className="
                  flex-1
                  py-2.5
                  px-3
                  rounded-xl
                  bg-slate-100
                  hover:bg-cyan-500
                  hover:text-slate-950
                  dark:bg-white/10
                  dark:hover:bg-cyan-400
                  dark:hover:text-slate-950
                  text-slate-900
                  dark:text-white
                  font-bold
                  text-xs
                  transition-all
                  duration-200
                  text-center
                  border
                  border-slate-200
                  dark:border-transparent
                "
              >
                {t("labs.specSheet")}
              </button>

              {app.googlePlayUrl && (
                <a
                  href={app.googlePlayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    p-2.5
                    rounded-xl
                    bg-slate-900
                    hover:bg-emerald-600
                    text-white
                    dark:bg-white/15
                    dark:hover:bg-emerald-500
                    transition-colors
                    flex
                    items-center
                    justify-center
                    shadow-sm
                  "
                  title="Abrir aplicación"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default HenkiLabsSection;
